import { CompanyChartType, FRChartType } from 'features/company/FRPage/types';
import moment from 'moment';
import { useMemo } from 'react';
import { color } from '@stockbit/ui';
import { Line } from 'react-chartjs-2';
import { ChartData, ChartOptions } from 'chart.js';

const fillColor = color.clearDay;
const lineColor = color.mountainMeadow;

interface ChartProps {
  chart: CompanyChartType;
  chartType: FRChartType;
}

const Chart = ({ chart, chartType }: ChartProps) => {
  const prices = chart?.chart_points;

  const labels = useMemo(() => {
    const data = [];
    const dateStart = moment().subtract(11, 'month');
    const dateEnd = moment();
    while (dateStart.diff(dateEnd, 'months') <= 0) {
      data.push(dateStart.format('MMM YYYY'));
      dateStart.add(1, 'month');
    }
    return data;
  }, [prices]);

  const chartData: ChartData<'line'> = useMemo(
    () => ({
      labels: prices.map((price) => {
        if (!price.formated_date && price.date === '0') return '';

        const formattedDate = moment(price.formated_date);
        const monthYear = formattedDate.format('MMM YYYY');
        const month = formattedDate.format('MMM');

        if (labels.includes(monthYear)) {
            labels.splice(labels.indexOf(monthYear), 1);
            return month;
          }
        return '';
      }),
      datasets: [
        {
          fill: true,
          tension: 0,
          backgroundColor: fillColor,
          borderColor: lineColor,
          borderCapStyle: 'butt',
          borderDashOffset: 0.0,
          borderJoinStyle: 'miter',
          borderWidth: 1.75,
          data: prices.map((pr) => {
            const number = pr.values.find((val) => val.key === chartType).value;
            const parsedNumber = parseFloat(number);
            return parseFloat(parsedNumber.toFixed(2));
          }),
        },
      ],
    }),
    [prices, chartType],
  );

  const options: ChartOptions<'line'> = useMemo(
    () => ({
      animation: {
        duration: 0,
      },
      resizeDelay: 0, // animation duration after a resize
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
      },
      maintainAspectRatio: false,
      scales: {
        y: {
          position: 'right',
          grid: {
            display: false,
            drawBorder: false,
          },
          ticks: {
            maxTicksLimit: 5,
            callback(val) {
              const parsedNumber = parseFloat(`${val}`);
              return `${parsedNumber.toFixed(2)}%`;
            },
          },
        },
        x: {
          grid: {
            display: false,
          },
          ticks: {
            align: 'start',
            maxTicksLimit: 5,
            maxRotation: 0,
            callback(val) {
              // eslint-disable-next-line react/no-this-in-sfc
              return this.getLabelForValue(val) || null;
            },
            autoSkip: true,
            // precision: 0,
          },
        },
      },
      elements: {
        point: {
          radius: 0,
        },
      },
      events: ['click'],
      onClick: () => {},
    }),
    [],
  );

  return <Line data={chartData} options={options} height={300} />;
};

export default Chart;
